'use client';

import type { ReactNode } from 'react';
import { cn, tv } from '../_lib/utils';
import { Anchor } from '../Anchor';
import { FormBlock, type FormBlockProps } from '../FormBlock';
import { isValidRichText } from '../_lib/isValidRichText';
import { Text } from '../Text';
import { IconSelection } from '@app/ui/IconSelection';
import { Divider } from '../Divider';
const styles = tv({
	slots: {
		content: 'grid gap-4 self-start [&_:first-child]:mt-0',
		form: '',
	},
	variants: {
		hasLinkList: {
			false: {
				content: 'w-full',
				form: ' w-full',
			},
		},
	},
});

const wrapperStyles = tv({
	slots: {
		base: 'grid gap-10',
	},
	variants: {
		contained: {
			true: {
				base: 'bg-form-background rounded-[1.5rem] relative gradient-border before:border before:border-transparent before:content-[""] before:absolute before:top-0 before:bottom-0 before:left-0 before:right-0 before:rounded-[1.5rem] before:pointer-events-none before:[background:var(--gradient-form-border)_border-box] px-5 py-10 md:p-12',
			},
		},
		hasLinkList: {
			true: {
				base: 'md:grid md:grid-cols-2 md:gap-10 @-xs:grid-cols-1! @-xl:grid-cols-2!',
			},
		},
	},
	compoundVariants: [
		{
			contained: true,
			hasLinkList: false,
			className: 'md:py-16',
		},
	],
});

export type FormProps = FormBlockProps & {
	className?: string;
	contained?: boolean;
	copy?: ReactNode;
	formTitle?: ReactNode;
	label?: string;
	linkList?: {
		title?: string;
		items: {
			label?: string;
			link: {
				label?: string;
				href?: string;
			};
		}[];
	};
	title?: ReactNode;
	emailAddress?: string;
};

export function Form(props: FormProps) {
	const contained = props.contained ?? false;
	const hasLinkList = props.linkList?.items && props.linkList.items.length > 0 ? true : false;
	const { content, form } = styles({ hasLinkList });
	const { base: wrapper } = wrapperStyles({ contained, hasLinkList });

	return (
		<div className={cn('py-5', props.className)} data-component='Form'>
			<div className={`${contained ? 'container-lg' : 'container-md'}`}>
				<div className={wrapper()}>
					{(props.label || props.title || props.copy || hasLinkList) && (
						<div className={content()}>
							{props.label && (
								<Text as='span' className='opacity-80 text-text-brand' styleType='label-sm'>
									{props.label}
								</Text>
							)}

							{props.title && (
								<Text as='h2' styleType='heading-sm'>
									{props.title}
								</Text>
							)}
							{hasLinkList && (
								<div className={`grid gap-4 p-6 rounded-lg bg-form-link-list-background/10 my-2 md:my-4`}>
									{props.linkList?.title && (
										<Text className='text-text-label' styleType='label-lg'>
											{props.linkList.title}
										</Text>
									)}
									<ul className={`grid gap-4`}>
										{props.linkList?.items.map((item, index) => {
											return (
												<li className={`grid gap-[0.375rem]`} key={`form-link-list-${index}`}>
													{item.label && (
														<Text as='span' className={`block`} styleType='body-sm'>
															{item.label}
														</Text>
													)}
													<div>
														<Anchor className={`link text-heading-3xs`} href={item.link.href}>
															{item.link.label}
														</Anchor>
													</div>
												</li>
											);
										})}
									</ul>
								</div>
							)}
							{isValidRichText(props.copy) && <Text styleType={hasLinkList ? 'body-sm' : 'editorial-md'}>{props.copy}</Text>}

							{props.emailAddress && (
								<>
									<Text as='span' className='text-text-label flex items-center gap-2 mt-5' styleType='label-sm'>
										<IconSelection icon='mail' />
										<Anchor href={`mailto:${props.emailAddress}`}>{props.emailAddress}</Anchor>
									</Text>
									<Divider padding={32} />
								</>
							)}
						</div>
					)}
					<div className={form()}>
						{props.formTitle && (
							<Text as='h3' className={`mb-4`} styleType='body-xl-medium'>
								{props.formTitle}
							</Text>
						)}
						<FormBlock
							hubspotFormId={props.hubspotFormId}
							buttonText={props.buttonText}
							successHeading={props.successHeading}
							successMessage={props.successMessage}
							successFile={props.successFile}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
